/** Width of the drawn page; the preview box is narrower, so it stays sharp on high-density screens. */
const ThumbWidth = 480;

type PdfDocument = Awaited<ReturnType<(typeof import("pdfjs-dist"))["getDocument"]>["promise"]>;

/**
 * PDF.js (Mozilla's PDF viewer) is loaded the first time it's needed, so pages without a PDF
 * receipt don't pay for it.
 */
async function openPdf(pdf: Blob): Promise<PdfDocument> {
  const [pdfjs, worker] = await Promise.all([
    import("pdfjs-dist"),
    import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return pdfjs.getDocument({ data: new Uint8Array(await pdf.arrayBuffer()) }).promise;
}

/** One page drawn as a PNG, `width` pixels wide. */
async function drawPage(document: PdfDocument, number: number, width: number): Promise<Blob> {
  const page = await document.getPage(number);
  const unscaled = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: width / unscaled.width });
  const canvas = window.document.createElement("canvas");
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("No canvas to draw the PDF on.");
  // White behind the page: some PDFs leave it transparent.
  context.fillStyle = "#fff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: context, viewport }).promise;
  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("No picture."))), "image/png"),
  );
}

/** A picture of a PDF's first page, for the receipt preview. */
export async function pdfThumbnail(pdf: Blob): Promise<Blob> {
  const document = await openPdf(pdf);
  try {
    return await drawPage(document, 1, ThumbWidth);
  } finally {
    void document.destroy();
  }
}

/** Every page of a PDF as a picture, `width` pixels wide, for the full-screen receipt viewer. */
export async function pdfPages(pdf: Blob, width: number): Promise<Blob[]> {
  const document = await openPdf(pdf);
  try {
    const pages: Blob[] = [];
    for (let number = 1; number <= document.numPages; number++)
      pages.push(await drawPage(document, number, width));
    return pages;
  } finally {
    void document.destroy();
  }
}
